import { createMemo, createSignal, For, Show, useContext } from "solid-js"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import { SessionContext } from "@/context/hanko"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Text } from "@/designSystem/components/text"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { FileUpload } from "@/designSystem/patterns/file-upload"
import { List } from "@/designSystem/patterns/list"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { responsivePaddingClass } from "@/designSystem/utility/responsivePadding.css"
import {
  generateNetscapeBookmarks,
  parseNetscapeBookmarks,
} from "@/lib/netscape-bookmarks"
import { AddStoryForm } from "./add-story-form"
import { listClass } from "./story-settings.css"

const RAINDROP_CLIENT_ID = import.meta.env.VITE_RAINDROP_CLIENT_ID
const OAUTH_URL = import.meta.env.VITE_OAUTH_URL
const CDN_ASSETS_URL = import.meta.env.VITE_CDN_ASSETS_URL
const FOLDER = "static"
const BACKEND_URL = import.meta.env.VITE_FIKA_BACKEND_URL

export function StoriesSettings() {
  return <Settings />
}

type Props = {
  topLevel?: boolean
}
export function Settings(props: Props) {
  const buttons = createMemo(() => {
    if (props.topLevel) return []

    return [
      {
        label: "bookmarks",
        href: "/bookmarks/all",
      } as const,
    ]
  })

  return (
    <MainLayout
      header={
        <Navigation hue="emerald" buttons={buttons()} title="add bookmarks" />
      }
    >
      <Box gap="32">
        <AddStoryForm />
        <Box class={responsivePaddingClass}>
          <Section
            title="import from other services"
            hue="emerald"
            iconName="download-cloud"
            description="import from other services like Raindrop, Pocket, Instapaper, and more."
          >
            <IntegrationsForm />
          </Section>
        </Box>
        <Box class={responsivePaddingClass}>
          <Section
            title="import/export html bookmarks"
            hue="emerald"
            iconName="archive"
            description="import bookmarks from browser exports or export your bookmarks to HTML files compatible with any browser."
          >
            <HtmlBookmarksForm />
          </Section>
        </Box>
        <Box class={responsivePaddingClass}>
          <Section
            title="download browser extension"
            hue="emerald"
            iconName="puzzle"
            description="bookmark stories directly from the following browsers."
          >
            <BrowsersForm />
          </Section>
        </Box>
        <Mobile />
      </Box>
    </MainLayout>
  )
}

function Mobile() {
  const platforms = [
    {
      title: "iOS",
      logo: `${CDN_ASSETS_URL}/${FOLDER}/ios.png?width=32`,
      alert:
        "unfortunately, iOS still does not support sharing links for this kind of applications, so you will have to open the app and paste them yourself.",
      description: (
        <Box>
          <p>The steps to add apps to the home screen are:</p>
          <ul class={listClass}>
            <li>
              Open the Share menu, available at the bottom or top of the
              browser.
            </li>
            <li>Click Add to Home Screen.</li>
            <li>Confirm the name of the app.</li>
            <li>Click Add.</li>
          </ul>
        </Box>
      ),
    },
    {
      title: "Android",
      logo: `${CDN_ASSETS_URL}/${FOLDER}/android.png?width=32`,
      description: (
        <Box>
          <p>
            Just use the application for a while and eventually you will be
            prompted to install the app.
          </p>
        </Box>
      ),
    },
  ]
  return (
    <Box class={responsivePaddingClass}>
      <Section
        title="install on mobile"
        hue="emerald"
        iconName="smartphone"
        description="bookmark stories you find while browsing on your mobile device and read them offline"
      >
        <Box tag="ul" gap="16">
          <For each={platforms}>
            {item => (
              <Well>
                <img src={item.logo} width="32" alt={item.title} />
                <Box gap="8">
                  <Text fontSize="l" fontWeight="500">
                    {item.title}
                  </Text>
                  {item.description}
                  <Show when={item.alert}>
                    {alert => (
                      <Box
                        padding={{
                          inline: "16",
                          block: "8",
                        }}
                        background="orange100"
                        borderRadius="16"
                      >
                        <Text fontSize="m" color="orange800">
                          {alert()}
                        </Text>
                      </Box>
                    )}
                  </Show>
                </Box>
              </Well>
            )}
          </For>
        </Box>
      </Section>
    </Box>
  )
}

function BrowsersForm() {
  const integrations = [
    {
      title: "Chrome",
      url: "https://chromewebstore.google.com/detail/fika-bar/igijfgpcajcnkdefnlfbjcpgbbfokcoh",
      logo: `${CDN_ASSETS_URL}/${FOLDER}/chrome-store.png?class=logo`,
    },
  ]

  return (
    <List
      list={integrations}
      href={item => item.url}
      content={item => (
        <Box fill flexDirection="row" gap="16">
          <img src={item.logo} alt={item.title} />
          <Box fill>
            <Text fontSize="m">{item.title}</Text>
          </Box>
        </Box>
      )}
    />
  )
}

function IntegrationsForm() {
  const getSession = useContext(SessionContext)
  // TODO: handle invalid session case
  const session = getSession()!
  const token = session.jwt!

  function commonParams(adapter: string) {
    const redirectUri = `${OAUTH_URL}/api/remote_bookmarks/auth/${adapter}`

    return `&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(token)}`
  }

  const integrations = [
    {
      title: "Raindrop",
      url: `https://api.raindrop.io/v1/oauth/authorize?client_id=${RAINDROP_CLIENT_ID}${commonParams("raindrop")}`,
      logo: `${CDN_ASSETS_URL}/${FOLDER}/raindrop.webp?class=logo`,
    },
    {
      title: "Pocket (RIP)",
      url: `${BACKEND_URL}/api/remote_bookmarks/start/pocket?state=${encodeURIComponent(token)}`,
      logo: `${CDN_ASSETS_URL}/${FOLDER}/pocket.webp?class=logo`,
    },
  ]

  return (
    <List
      list={integrations}
      href={item => item.url}
      content={item => (
        <Box fill flexDirection="row" gap="16">
          <img src={item.logo} alt={item.title} />
          <Box fill>
            <Text fontSize="m">{item.title}</Text>
          </Box>
        </Box>
      )}
    />
  )
}

function HtmlBookmarksForm() {
  const { replicache, user, db } = useAppContext()
  const [importing, setImporting] = createSignal(false)
  const [exporting, setExporting] = createSignal(false)

  async function handleImport(file: File | null) {
    if (!file) return

    setImporting(true)

    try {
      const text = await file.text()
      const rawBookmarks = parseNetscapeBookmarks(text)

      const bookmarks = rawBookmarks.flatMap(rawBookmark => {
        const existingStory = db.story.findBy({ url: rawBookmark.url })

        if (existingStory) {
          const story_id = existingStory.id
          const existingBookmark = db.bookmark.findBy({ story_id })
          if (existingBookmark) return []
        }

        return {
          bookmark_id: ulid(),
          story_id: ulid(),
          user_id: user.id,
          url: rawBookmark.url,
          dc_title: rawBookmark.title || null,
          dc_description: rawBookmark.description || null,
          dc_created_at: rawBookmark.addDate?.toISOString() || null,
          metadated_at: null,
          logo_url: null,
          image_url: null,
          feed_url: null,
          dc_identifier: rawBookmark.url,
        }
      })

      await replicache.mutate.importBookmarks({
        bookmarks,
        skipped: rawBookmarks.length - bookmarks.length,
      })
    } catch (error) {
      console.error("Import error:", error)
      replicache.mutate.initLog({
        id: ulid(),
        title: "Error importing bookmarks. Please check the file format.",
        status: "Failed",
        created_at: new Date().toISOString(),
      })
    } finally {
      setImporting(false)
    }
  }

  function handleExport() {
    setExporting(true)

    try {
      const bookmarks = db.bookmark.all()

      const netscapeBookmarks = bookmarks.flatMap(bookmark => {
        const story = db.story.get(bookmark.story_id)
        if (!story) return []

        return {
          url: story.url,
          title: story.dc_title || story.url,
          description: story.dc_description || undefined,
          addDate: new Date(bookmark.bookmarked_at),
        } as const
      })

      if (netscapeBookmarks.length === 0) {
        setExporting(false)
        replicache.mutate.initLog({
          id: ulid(),
          title: "No bookmarks to export.",
          status: "Failed",
          created_at: new Date().toISOString(),
        })
        return
      }

      const html = generateNetscapeBookmarks(netscapeBookmarks)
      const blob = new Blob([html], { type: "text/html;charset=utf-8" })
      const url = URL.createObjectURL(blob)

      const a = document.createElement("a")
      a.href = url
      a.download = `fika-bookmarks-${new Date().toISOString().split("T")[0]}.html`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      replicache.mutate.initLog({
        id: ulid(),
        title: `Exported ${netscapeBookmarks.length} bookmarks.`,
        status: "Success",
        created_at: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Export error:", error)
      replicache.mutate.initLog({
        id: ulid(),
        title: "Error exporting bookmarks.",
        status: "Failed",
        created_at: new Date().toISOString(),
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box gap="24" fill>
      <Well>
        <Box gap="24" fill>
          <Box>
            <Text fontSize="m" fontWeight="500">
              Import HTML Bookmarks
            </Text>
            <Text fontSize="s" color="sand700">
              Select an HTML bookmark file (usually exported from browsers like
              Chrome, Firefox, Safari, etc.)
            </Text>
          </Box>
          <FileUpload
            onUpload={handleImport}
            text={importing() ? "importing..." : "choose file"}
            hue="emerald"
            accept=".html,.htm"
            iconName="upload-cloud"
            disabled={importing()}
          />
        </Box>
      </Well>
      <Well>
        <Box gap="24" fill>
          <Box>
            <Text fontSize="m" fontWeight="500">
              Export HTML Bookmarks
            </Text>
            <Text fontSize="s" color="sand700">
              Download all your bookmarks as an HTML file that can be imported
              into any browser or bookmark manager.
            </Text>
          </Box>
          <Box alignItems="flex-end">
            <Button
              hue="emerald"
              iconName="download-cloud"
              onClick={handleExport}
              disabled={exporting()}
            >
              {exporting() ? "exporting..." : "download file"}
            </Button>
          </Box>
        </Box>
      </Well>
    </Box>
  )
}
